/**
 * Week-based time utilities.
 * Weeks run Monday (start) to Sunday (end).
 */

/**
 * Get the Monday of the week containing the given date.
 */
export const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Sunday = go back 6, else go back to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get the Sunday of the week containing the given date.
 */
export const getWeekEnd = (date) => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

/**
 * Get ISO week number (1-53).
 */
export const getISOWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const yearStart = new Date(d.getFullYear(), 0, 4);
  return Math.ceil(((d - yearStart) / 86400000 + yearStart.getDay() + 6) / 7);
};

/**
 * Navigate to previous/next period.
 */
export const navigateWeek = (date, direction) => {
  const d = new Date(date);
  d.setDate(d.getDate() + direction * 7);
  return getWeekStart(d);
};

export const navigateMonth = (date, direction) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + direction);
  d.setDate(1);
  return d;
};

export const navigateQuarter = (date, direction) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + direction * 3);
  d.setDate(1);
  return d;
};

export const navigateYear = (date, direction) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + direction);
  d.setDate(1);
  return d;
};

/**
 * Get all weeks that fall within a month.
 * Returns array of { weekNumber, start (Date), end (Date) }.
 */
export const getWeeksInMonth = (year, month) => {
  const weeks = [];
  const firstDay = new Date(year, month, 1);
  let weekStart = getWeekStart(firstDay);

  while (weekStart.getMonth() <= month && weekStart.getFullYear() <= year ||
         (weekStart.getMonth() === 11 && month === 0 && weekStart.getFullYear() < year)) {
    const weekEnd = getWeekEnd(weekStart);
    // Include week if any part of it falls in the target month
    if (weekEnd.getMonth() >= month && weekStart.getMonth() <= month && weekStart.getFullYear() === year ||
        weekEnd.getFullYear() === year && weekEnd.getMonth() >= month && weekStart.getMonth() <= month) {
      weeks.push({
        weekNumber: getISOWeekNumber(weekStart),
        start: new Date(weekStart),
        end: new Date(weekEnd),
      });
    }
    weekStart = new Date(weekStart);
    weekStart.setDate(weekStart.getDate() + 7);
    // Safety: stop after 6 iterations
    if (weeks.length >= 6) break;
  }
  return weeks;
};

/**
 * Get all weeks in a quarter (3 months).
 * Quarter: 0 = Q1 (Jan-Mar), 1 = Q2 (Apr-Jun), 2 = Q3 (Jul-Sep), 3 = Q4 (Oct-Dec)
 */
export const getQuarterMonths = (year, quarter) => {
  const startMonth = quarter * 3;
  return [
    { month: startMonth, label: new Date(year, startMonth, 1).toLocaleString('default', { month: 'long' }) },
    { month: startMonth + 1, label: new Date(year, startMonth + 1, 1).toLocaleString('default', { month: 'long' }) },
    { month: startMonth + 2, label: new Date(year, startMonth + 2, 1).toLocaleString('default', { month: 'long' }) },
  ];
};

export const getQuarter = (date) => Math.floor(new Date(date).getMonth() / 3);

/**
 * Format labels.
 */
export const formatWeekLabel = (date) => {
  const start = getWeekStart(date);
  const end = getWeekEnd(date);
  const weekNum = getISOWeekNumber(start);
  const opts = { month: 'short', day: 'numeric' };
  const yearOpts = { ...opts, year: 'numeric' };
  const startStr = start.toLocaleDateString('en-US', opts);
  const endStr = end.toLocaleDateString('en-US', yearOpts);
  return `Week ${weekNum}: ${startStr} – ${endStr}`;
};

export const formatMonthLabel = (year, month) => {
  return new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
};

export const formatQuarterLabel = (year, quarter) => {
  return `Q${quarter + 1} ${year}`;
};

/**
 * Check if a task overlaps a given date range.
 * Task must have startDate. endDate defaults to startDate if missing.
 */
export const doesTaskOverlapRange = (task, rangeStart, rangeEnd) => {
  if (!task.startDate) return false;
  const taskStart = new Date(task.startDate);
  const taskEnd = task.endDate ? new Date(task.endDate) : taskStart;
  const rStart = new Date(rangeStart);
  const rEnd = new Date(rangeEnd);
  return taskStart <= rEnd && taskEnd >= rStart;
};

/**
 * Count tasks that overlap a range.
 */
export const countTasksInRange = (tasks, rangeStart, rangeEnd) => {
  return (tasks || []).filter(t => doesTaskOverlapRange(t, rangeStart, rangeEnd)).length;
};

/**
 * Get tasks that overlap a range.
 */
export const getTasksInRange = (tasks, rangeStart, rangeEnd) => {
  return (tasks || []).filter(t => doesTaskOverlapRange(t, rangeStart, rangeEnd));
};

/**
 * Get the next N weeks starting from a date.
 * Returns array of { start: Date (Monday), end: Date (Sunday 23:59:59) }
 */
export const getNextNWeeks = (n, fromDate = new Date()) => {
  const weeks = [];
  let current = getWeekStart(fromDate);
  for (let i = 0; i < n; i++) {
    weeks.push({ start: new Date(current), end: getWeekEnd(current) });
    current = new Date(current.getTime() + 7 * 24 * 60 * 60 * 1000);
  }
  return weeks;
};
